"use client";

import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import type { FormField } from "@/lib/database";
import { useTranslations } from "next-intl";

export const OPERATORS = [
	{ value: "equals", label: "equals" },
	{ value: "not_equals", label: "not equals" },
	{ value: "contains", label: "contains" },
	{ value: "not_contains", label: "does not contain" },
	{ value: "is_checked", label: "is checked" },
	{ value: "is_not_checked", label: "is not checked" },
	{ value: "greater_than", label: "greater than" },
	{ value: "less_than", label: "less than" },
] as const;

type VisibilityConfig = NonNullable<
	NonNullable<FormField["settings"]>["conditionalVisibility"]
>;

interface ConditionalVisibilitySettingsProps {
	field: FormField;
	onUpdateSettings: (updates: Partial<FormField["settings"]>) => void;
}

const NO_VALUE_OPERATORS = new Set(["is_checked", "is_not_checked"]);

export function ConditionalVisibilitySettings({
	field,
	onUpdateSettings,
}: ConditionalVisibilitySettingsProps) {

	const t = useTranslations("fieldSettings");	const config = field.settings?.conditionalVisibility ?? {
		enabled: false,
		match: "all" as const,
		conditions: [],
	};

	const updateConfig = (updates: Partial<VisibilityConfig>) => {
		onUpdateSettings({
			conditionalVisibility: {
				...config,
				...updates,
			},
		});
	};

	const updateCondition = (
		index: number,
		updates: Partial<VisibilityConfig["conditions"][number]>
	) => {
		const conditions = config.conditions.map((condition, i) =>
			i === index ? { ...condition, ...updates } : condition
		);
		updateConfig({ conditions });
	};

	const removeCondition = (index: number) => {
		updateConfig({
			conditions: config.conditions.filter((_, i) => i !== index),
		});
	};

	const addCondition = () => {
		updateConfig({
			conditions: [
				...config.conditions,
				{ fieldId: "", operator: "equals", value: "" },
			],
		});
	};

	const operatorNeedsValue = (operator: string) =>
		!NO_VALUE_OPERATORS.has(operator);

	return (
		<Card className="gap-2 p-4 shadow-none">
			<CardHeader className="p-0">
				<CardTitle className="flex items-center gap-2 text-lg">
					{t("conditionalVisibilityTitle")}
				</CardTitle>
			</CardHeader>
			<CardContent className="flex flex-col gap-4 p-0">
				<p className="text-muted-foreground text-xs">
					{t("showWhenConditions")}
				</p>

				<div className="flex items-center justify-between gap-4">
					<Label className="font-medium text-sm" htmlFor="cvEnabled">
						{t("enableCondition")}
					</Label>
					<Switch
						checked={config.enabled}
						id="cvEnabled"
						onCheckedChange={(checked) =>
							updateConfig({ enabled: Boolean(checked) })
						}
					/>
				</div>

				{config.enabled && (
					<>
						<div className="flex flex-col gap-2">
							<Label className="font-medium text-sm">{t("match")}</Label>
							<Select
								value={config.match}
								onValueChange={(value) =>
									updateConfig({ match: value as "all" | "any" })
								}
							>
								<SelectTrigger className="w-full">
									<SelectValue placeholder={t("selectMatchMode")} />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="all">{t("allConditions")}</SelectItem>
									<SelectItem value="any">{t("anyCondition")}</SelectItem>
								</SelectContent>
							</Select>
						</div>

						<div className="flex flex-col gap-3">
							{config.conditions.map((condition, index) => (
								<div
									className="flex flex-col gap-2 rounded-md border p-3"
									key={index}
								>
									<div className="flex items-center justify-between gap-2">
										<span className="font-medium text-xs text-muted-foreground">
											{t("conditionN", { n: index + 1 })}
										</span>
										<Button
											aria-label={t("removeCondition")}
											className="size-7"
											onClick={() => removeCondition(index)}
											size="icon"
											type="button"
											variant="ghost"
										>
											<Trash2 className="size-3.5" />
										</Button>
									</div>

									<Input
										placeholder={t("fieldIdToCheck")}
										value={condition.fieldId}
										onChange={(e) =>
											updateCondition(index, {
												fieldId: e.target.value,
											})
										}
									/>

									<Select
										value={condition.operator}
										onValueChange={(value) =>
											updateCondition(index, {
												operator: value as VisibilityConfig["conditions"][number]["operator"],
											})
										}
									>
										<SelectTrigger className="w-full">
											<SelectValue placeholder={t("operator")} />
										</SelectTrigger>
										<SelectContent>
											{OPERATORS.map((op) => (
												<SelectItem key={op.value} value={op.value}>
													{t(`operators.${op.value}`)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>

									{operatorNeedsValue(condition.operator) && (
										<Input
											placeholder={t("expectedValue")}
											value={String(condition.value ?? "")}
											onChange={(e) =>
												updateCondition(index, {
													value: e.target.value,
												})
											}
										/>
									)}
								</div>
							))}

							<Button
								className="w-fit"
								onClick={addCondition}
								size="sm"
								type="button"
								variant="outline"
							>
								<Plus className="size-4" /> {t("addCondition")}
							</Button>
						</div>
					</>
				)}
			</CardContent>
		</Card>
	);
}
